// Polyfill and shim includes
import 'core-js/actual';
import 'airbnb-js-shims';
import 'eventsource-polyfill';
import './assets/helpers/polyfills/canvas-to-blob';
import './assets/helpers/polyfills/canvas2Blob';
import cssVars from 'css-vars-ponyfill';

import React, { useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import store from './store/store';
import { BrowserRouter } from 'react-router-dom';
import { init, captureException } from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { SplitFactory, SplitContext, useTreatments } from '@splitsoftware/splitio-react';

import './index.css';
import './theme.css';
import App from './App';
import registerServiceWorker, { unregister } from './registerServiceWorker';
import config from './constants/config';
import { getVanityRedirectTarget } from './assets/helpers/vanityRedirect';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from './components/UI/Alert/ErrorFallback'

cssVars({});

// --- Stale-client recovery -------------------------------------------------
// Every build emits new hashed chunk filenames; old ones are pruned from the
// server after the retention window. A client that still references an old
// chunk hash -- a long-open tab, or a service-worker cache that outlived the
// pruned files -- would hit a 404 / ChunkLoadError when a code-split chunk is
// requested. When that happens, drop the service-worker cache (which may still
// be serving a stale app shell) and reload ONCE into the current build.
(function setupChunkErrorRecovery() {
  if (typeof window === 'undefined') {
    return;
  }
  const CHUNK_ERROR =
    /Loading chunk [\w./-]+ failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
  // Count recovery reloads in this tab session (sessionStorage survives reloads).
  // Capped so a build that is broken for everyone -- e.g. a chunk genuinely
  // missing on the server -- can never reload-loop: it reloads at most
  // MAX_RELOADS times, then gives up and leaves the (broken) page in place.
  // The normal stale-client case is fixed by a single reload into the current
  // build, so a cap of 1 is enough. (Do NOT re-arm this on a timer: a still-
  // broken page would re-trigger and loop.)
  const KEY = 'chunk-reload-attempts';
  const MAX_RELOADS = 1;
  let recovering = false;

  async function recover() {
    if (recovering) {
      return;
    }
    const attempts = parseInt(window.sessionStorage.getItem(KEY) || '0', 10) || 0;
    if (attempts >= MAX_RELOADS) {
      return; // already recovered once this session; do not loop
    }
    recovering = true;
    window.sessionStorage.setItem(KEY, String(attempts + 1));
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches && window.caches.keys) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      }
    } catch (e) {
      /* best effort -- reload regardless */
    }
    window.location.reload();
  }

  function handleError(msg) {
    if (CHUNK_ERROR.test(msg)) {
      recover();
    }
  }

  window.addEventListener('error', (event) => {
    handleError((event && (event.message || (event.error && event.error.message))) || '');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason;
    handleError((reason && (reason.message || reason.name)) || String(reason || ''));
  });
})();

const ErrorBoundaryWrapper = ({ children }) => {
  // access status properties via the SplitContext
  const { isReady } = useContext(SplitContext);
  // evaluate feature flags with the `useTreatments` hook
  const treatments = useTreatments(config.FEATURE_FLAGS);
  const { treatment } = treatments['isSentryEnabledForWeb'];
  const isSentryEnabledForWeb = treatment === 'on';

  useEffect(() => {
    if (isReady && isSentryEnabledForWeb && config.SENTRY_DNS && config.SENTRY_RELEASE) {
      // inject sentry
      init({
        dsn: config.SENTRY_DNS,
        integrations: [new Integrations.BrowserTracing()],
        tracesSampleRate: config.ENVIRONMENT === 'production' ? 0.15 : 1.0,
        environment: config.ENVIRONMENT,
        release: `${config.SENTRY_RELEASE}@${config.VERSION}`,
        // whitelistUrls: [/bikers|widower\.dating/],
        // no localhost:3000 to reduce spam notifications
        blacklistUrls: [/taboola\.com|adsbackend\.com|localhost:3000|\.clarity\.ms|\.snapchat\.com/],
        // Fix for UnhandledRejection microsoft bots cause
        // https://sentry.io/organizations/nb-social/issues/2460614616/?project=5726384&query=is%3Aunresolved&statsPeriod=14d
        // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
        ignoreErrors: [
          // 'Non-Error exception captured',
          // 'Non-Error promise rejection captured',
          'Loading chunk 1 failed.',
          'Loading chunk 2 failed.',
          'Loading chunk 3 failed.',
          'Loading chunk 4 failed.',
          'Loading chunk 5 failed.',
          'Loading chunk 6 failed.',
          'Loading chunk 7 failed.',
          'Loading chunk 8 failed.',
          'Loading chunk 9 failed.',
          'Loading chunk 21 failed.',
          // "Can't find variable: _AutofillCallbackHandler",
          // "Identifier 'change_ua' has already been declared",
        ],
      });
    }
  }, [isReady, isSentryEnabledForWeb]);

  const myErrorHandler = (error, info) => {
    if (isReady && isSentryEnabledForWeb && config.SENTRY_DNS && config.SENTRY_RELEASE) {
      captureException(error, info.componentStack);
    }
    // eslint-disable-next-line no-console
    console.error('JSErrorBoundary !!!!:', error, info.componentStack);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={myErrorHandler}>
      {children}
    </ErrorBoundary>
  );
}

const SPLITIO_CONFIG = {
  core: {
    authorizationKey:
      config.ENVIRONMENT === 'production'
        ? config.SPLITIO_AUTH_KEY_PROD
        : config.SPLITIO_AUTH_KEY_DEV,
    key: config.SPLITIO_USER_KEY,
  }
};

const Application = () => (
  <SplitFactory config={SPLITIO_CONFIG}>
    <Provider store={store}>
      {/* <ErrorBoundaryWrapper> */}
      <BrowserRouter>
        <App />
      </BrowserRouter>
      {/* </ErrorBoundaryWrapper> */}
    </Provider>
  </SplitFactory>
);

// Vanity URL redirects (e.g. golf.singles/browsesingles -> /signup?aff_...).
// Handled here, before React renders, so there is no app-shell flash and the
// destination loads as a fresh full page (so affiliate params are parsed).
// The service worker still serves the app shell for the destination; the query
// params survive in window.location and are read on boot. See vanityRedirect.js.
const vanityTarget = getVanityRedirectTarget(
  window.location.hostname,
  window.location.pathname
);

if (vanityTarget) {
  window.location.replace(vanityTarget);
} else {
  ReactDOM.render(<Application />, document.getElementById('root'));

  if (
    (config.ENVIRONMENT === 'production' || config.ENVIRONMENT === 'dev') &&
    config.ENABLE_SERVICE_WORKER === true
  ) {
    registerServiceWorker();
  } else {
    unregister();
  }
}
